import React, { useState } from "react";
import styled from "styled-components";
import { Dialog } from "@material-ui/core";
import { IPageSectionResponse } from "models/response/page-section-response";
import { SectionTitle } from "../custom-page.styles";

const GalleryContainer = styled.div`
  margin-bottom: 30px;
`;

const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 15px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 10px;
  }
`;

const GalleryItem = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: 8px;
  cursor: pointer;
  aspect-ratio: 4/3;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }
`;

const GalleryImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ImageCaption = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.8), transparent);
  color: white;
  padding: 15px 10px 10px;
  font-size: 14px;
  font-weight: 500;
`;

const LightboxDialog = styled(Dialog)`
  .MuiDialog-paper {
    background: rgba(0, 0, 0, 0.95);
    max-width: 90vw;
    max-height: 90vh;
  }
`;

const LightboxContent = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
`;

const LightboxImage = styled.img`
  max-width: 100%;
  max-height: 70vh;
  object-fit: contain;
`;

const LightboxCaption = styled.div`
  color: white;
  margin-top: 20px;
  text-align: center;
  max-width: 600px;
`;

const LightboxTitle = styled.h3`
  font-size: 20px;
  margin-bottom: 8px;
`;

const LightboxDescription = styled.p`
  font-size: 14px;
  color: #ccc;
  line-height: 1.5;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  font-size: 24px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

interface ImageGallerySectionProps {
    section: IPageSectionResponse;
}

export const ImageGallerySection: React.FC<ImageGallerySectionProps> = ({ section }) => {
    const [selectedImage, setSelectedImage] = useState<number | null>(null);

    if (!section.items || section.items.length === 0) {
        return null;
    }

    const sortedItems = [...section.items].sort((a, b) => a.order - b.order);
    const selectedItem = selectedImage !== null ? sortedItems[selectedImage] : null;

    return (
        <GalleryContainer>
            {section.title && <SectionTitle>{section.title}</SectionTitle>}
            <GalleryGrid>
                {sortedItems.map((item, index) => (
                    <GalleryItem key={item.id} onClick={() => setSelectedImage(index)}>
                        <GalleryImage src={item.imageUrl || ''} alt={item.title || ''} />
                        {item.title && <ImageCaption>{item.title}</ImageCaption>}
                    </GalleryItem>
                ))}
            </GalleryGrid>

            <LightboxDialog
                open={selectedImage !== null}
                onClose={() => setSelectedImage(null)}
                maxWidth={false}
            >
                {selectedItem && (
                    <LightboxContent>
                        <CloseButton onClick={() => setSelectedImage(null)}>×</CloseButton>
                        <LightboxImage src={selectedItem.imageUrl || ''} alt={selectedItem.title || ''} />
                        {(selectedItem.title || selectedItem.description) && (
                            <LightboxCaption>
                                {selectedItem.title && <LightboxTitle>{selectedItem.title}</LightboxTitle>}
                                {selectedItem.description && (
                                    <LightboxDescription>{selectedItem.description}</LightboxDescription>
                                )}
                            </LightboxCaption>
                        )}
                    </LightboxContent>
                )}
            </LightboxDialog>
        </GalleryContainer>
    );
};
